import React, { useState, useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { MessageCircle, X, Send, Bot, User, Loader2, Sparkles, ChevronDown, Mic, MicOff, Volume2, VolumeX, Mail, Check } from 'lucide-react';
import api from '../utils/api';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const AIChatBubble = () => {
  const location = useLocation();
  const [user, setUser] = useState(null);
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [sessionId, setSessionId] = useState(null);
  const [assistant, setAssistant] = useState({ name: 'Zara', emoji: '👩', voice: 'nova' });
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [voiceEnabled, setVoiceEnabled] = useState(true);
  const [showShareModal, setShowShareModal] = useState(false);
  const [shareEmail, setShareEmail] = useState('');
  const [shareStatus, setShareStatus] = useState(null); // 'sending', 'success', 'error'
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const audioRef = useRef(null);

  // Hide AI chat bubble on live stream pages to avoid blocking UI
  const isLiveStreamPage = location.pathname.startsWith('/live/');

  // Get current user
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const token = localStorage.getItem('token');
        if (token) {
          const response = await api.get('/auth/me');
          setUser(response.data);
        }
      } catch (error) {
        console.log('Not logged in');
      }
    };
    fetchUser();
  }, []);

  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  // Initial greeting - fetch assistant first
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      // Make an initial API call to get assigned assistant
      const initChat = async () => {
        try {
          const response = await fetch(`${API_URL}/api/help/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              message: "hi",
              session_id: sessionId,
              user_email: user?.email,
              user_id: user?.id
            })
          });
          const data = await response.json();
          
          if (data.session_id) {
            setSessionId(data.session_id);
          }
          if (data.assistant) {
            setAssistant(data.assistant);
          }
          
          // Set the greeting with correct assistant name
          const assistantName = data.assistant?.name || 'Zara';
          setMessages([{
            role: 'assistant',
            content: `Hi${user ? ` ${user.first_name || ''}` : ''}! 👋 I'm ${assistantName}, your BidZuu support assistant. I'm here to help you with:\n\n• Password reset\n• Returns & refunds\n• Order status\n• Shipping questions\n• Account issues\n• How to buy/sell\n• Payment help\n• Live streaming\n\nHow can I help you today?`
          }]);
        } catch (error) {
          // Fallback greeting if API fails
          setMessages([{
            role: 'assistant',
            content: `Hi${user ? ` ${user.first_name || ''}` : ''}! 👋 I'm ${assistant.name}, your BidZuu support assistant. How can I help you today?`
          }]);
        }
      };
      initChat();
    }
  }, [isOpen]);

  // Start voice recording
  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream, { mimeType: 'audio/webm' });
      audioChunksRef.current = [];
      
      mediaRecorderRef.current.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };
      
      mediaRecorderRef.current.onstop = async () => {
        const audioBlob = new Blob(audioChunksRef.current, { type: 'audio/webm' });
        stream.getTracks().forEach(track => track.stop());
        await sendVoiceMessage(audioBlob);
      };
      
      mediaRecorderRef.current.start();
      setIsRecording(true);
    } catch (error) {
      console.error('Microphone access denied:', error);
      alert('Please allow microphone access to use voice chat.');
    }
  };

  // Stop voice recording
  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  };

  // Send voice message
  const sendVoiceMessage = async (audioBlob) => {
    setIsLoading(true);
    setMessages(prev => [...prev, { role: 'user', content: '🎤 Voice message...', isVoice: true }]);

    try {
      const formData = new FormData();
      formData.append('audio', audioBlob, 'voice.webm');
      if (sessionId) {
        formData.append('session_id', sessionId);
      }
      if (user?.email) {
        formData.append('user_email', user.email);
      }

      const response = await fetch(`${API_URL}/api/help/voice/chat`, {
        method: 'POST',
        body: formData
      });

      const data = await response.json();

      if (!data.success) {
        throw new Error(data.detail || 'Voice chat failed');
      }

      // Update the user message with transcribed text
      setMessages(prev => {
        const updated = [...prev];
        const lastUserIdx = updated.findLastIndex(m => m.role === 'user');
        if (lastUserIdx !== -1) {
          updated[lastUserIdx] = { 
            role: 'user', 
            content: data.user_text || '🎤 Voice message',
            isVoice: true 
          };
        }
        return updated;
      });

      if (data.session_id) {
        setSessionId(data.session_id);
      }
      if (data.assistant) {
        setAssistant(data.assistant);
      }

      // Add AI response
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.ai_text,
        audio: data.audio,
        isVoice: true
      }]);

      // Auto-play voice response if enabled
      if (voiceEnabled && data.audio) {
        playAudio(data.audio);
      }

    } catch (error) {
      console.error('Voice chat error:', error);
      setMessages(prev => {
        const updated = [...prev];
        const lastUserIdx = updated.findLastIndex(m => m.role === 'user');
        if (lastUserIdx !== -1) {
          updated[lastUserIdx] = { role: 'user', content: '🎤 (Voice failed to process)', isVoice: true };
        }
        return [...updated, { 
          role: 'assistant', 
          content: 'Sorry, I had trouble understanding that. Please try again or type your message.'
        }];
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Play audio response
  const playAudio = (base64Audio) => {
    try {
      const audioData = `data:audio/mp3;base64,${base64Audio}`;
      if (audioRef.current) {
        audioRef.current.pause();
      }
      audioRef.current = new Audio(audioData);
      audioRef.current.onplay = () => setIsPlaying(true);
      audioRef.current.onended = () => setIsPlaying(false);
      audioRef.current.onerror = () => setIsPlaying(false);
      audioRef.current.play();
    } catch (error) {
      console.error('Audio playback error:', error);
    }
  };

  // Stop audio playback
  const stopAudio = () => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.currentTime = 0;
      setIsPlaying(false);
    }
  };

  // Share transcript via email
  const shareTranscript = async () => {
    if (!shareEmail.trim()) return;
    
    setShareStatus('sending');
    try {
      const response = await fetch(`${API_URL}/api/help/share-transcript`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          email: shareEmail.trim(),
          messages: messages,
          assistant_name: assistant.name
        })
      });
      
      const data = await response.json();
      
      if (response.ok && data.success) {
        setShareStatus('success');
        setTimeout(() => {
          setShowShareModal(false);
          setShareEmail('');
          setShareStatus(null);
        }, 2000);
      } else {
        setShareStatus('error');
        setTimeout(() => setShareStatus(null), 3000);
      }
    } catch (error) {
      console.error('Share error:', error);
      setShareStatus('error');
      setTimeout(() => setShareStatus(null), 3000);
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    setInput('');
    setMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setIsLoading(true);

    try {
      const response = await fetch(`${API_URL}/api/help/chat`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          message: userMessage,
          session_id: sessionId,
          user_email: user?.email,
          user_id: user?.id
        })
      });

      const data = await response.json();
      
      if (data.session_id && !sessionId) {
        setSessionId(data.session_id);
      }
      
      // Update assistant if returned from API
      if (data.assistant) {
        setAssistant(data.assistant);
      }

      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: data.response,
        action: data.action_taken
      }]);

    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again or contact support@bidzuu.com.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const quickActions = [
    { label: 'Reset Password', message: 'I need to reset my password' },
    { label: 'Track Order', message: 'I want to track my order' },
    { label: 'Start Return', message: 'I want to return an item' },
    { label: 'Go Live Help', message: 'How do I start a live stream?' },
  ];

  // Don't render on live stream pages to avoid blocking UI
  if (isLiveStreamPage) {
    return null;
  }

  return (
    <>
      {/* Chat Bubble Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-all duration-300 ${
          isOpen 
            ? 'bg-gray-700 hover:bg-gray-600 rotate-0' 
            : 'bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 animate-pulse'
        }`}
        data-testid="ai-chat-bubble"
      >
        {isOpen ? (
          <X className="h-6 w-6 text-white" />
        ) : (
          <MessageCircle className="h-6 w-6 text-white" />
        )}
      </button>

      {/* Chat Window */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-96 max-w-[calc(100vw-48px)] h-[500px] max-h-[calc(100vh-120px)] bg-white rounded-2xl shadow-2xl flex flex-col overflow-hidden border border-gray-200"
          data-testid="ai-chat-window"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-emerald-500 to-teal-500 px-4 py-3 flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white/20 rounded-full flex items-center justify-center">
                <span className="text-white text-lg">{assistant.emoji}</span>
              </div>
              <div>
                <h3 className="text-white font-semibold">{assistant.name} from BidZuu</h3>
                <p className="text-emerald-100 text-xs">Online • Usually replies instantly</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              {messages.length > 1 && (
                <button 
                  onClick={() => setShowShareModal(true)}
                  className="text-white/80 hover:text-white transition p-1.5 hover:bg-white/10 rounded-full"
                  title="Email transcript"
                  data-testid="ai-share-btn"
                >
                  <Mail className="h-4 w-4" />
                </button>
              )}
              <button 
                onClick={() => setIsOpen(false)}
                className="text-white/80 hover:text-white transition"
              >
                <ChevronDown className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Share Modal */}
          {showShareModal && (
            <div className="absolute inset-0 bg-black/50 z-10 flex items-center justify-center p-4">
              <div className="bg-white rounded-xl shadow-xl w-full max-w-sm p-5" data-testid="share-modal">
                <div className="flex items-center justify-between mb-4">
                  <h4 className="font-semibold text-gray-900">Email Transcript</h4>
                  <button 
                    onClick={() => { setShowShareModal(false); setShareStatus(null); }}
                    className="text-gray-400 hover:text-gray-600"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                
                {shareStatus === 'success' ? (
                  <div className="text-center py-4">
                    <div className="w-12 h-12 bg-emerald-100 rounded-full flex items-center justify-center mx-auto mb-3">
                      <Check className="h-6 w-6 text-emerald-600" />
                    </div>
                    <p className="text-emerald-600 font-medium">Transcript sent!</p>
                    <p className="text-gray-500 text-sm mt-1">Check your inbox</p>
                  </div>
                ) : (
                  <>
                    <p className="text-gray-600 text-sm mb-4">
                      Send a copy of this conversation to your email for future reference.
                    </p>
                    <input
                      type="email"
                      value={shareEmail}
                      onChange={(e) => setShareEmail(e.target.value)}
                      placeholder="Enter email address"
                      className="w-full px-4 py-2.5 border border-gray-200 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                      disabled={shareStatus === 'sending'}
                      data-testid="share-email-input"
                    />
                    {shareStatus === 'error' && (
                      <p className="text-red-500 text-xs mt-2">Failed to send. Please try again.</p>
                    )}
                    <div className="flex gap-2 mt-4">
                      <button
                        onClick={() => { setShowShareModal(false); setShareStatus(null); }}
                        className="flex-1 px-4 py-2 border border-gray-200 rounded-lg text-gray-600 hover:bg-gray-50 transition text-sm"
                      >
                        Cancel
                      </button>
                      <button
                        onClick={shareTranscript}
                        disabled={!shareEmail.trim() || shareStatus === 'sending'}
                        className="flex-1 px-4 py-2 bg-emerald-500 text-white rounded-lg hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed transition text-sm flex items-center justify-center gap-2"
                        data-testid="share-send-btn"
                      >
                        {shareStatus === 'sending' ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            Sending...
                          </>
                        ) : (
                          <>
                            <Mail className="h-4 w-4" />
                            Send
                          </>
                        )}
                      </button>
                    </div>
                  </>
                )}
              </div>
            </div>
          )}

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4 bg-gray-50">
            {messages.map((msg, idx) => (
              <div 
                key={idx} 
                className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                <div className={`flex gap-2 max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : ''}`}>
                  <div className={`w-8 h-8 rounded-full flex-shrink-0 flex items-center justify-center ${
                    msg.role === 'user' 
                      ? 'bg-emerald-500 text-white' 
                      : 'bg-gradient-to-br from-purple-500 to-indigo-500 text-white'
                  }`}>
                    {msg.role === 'user' ? <User className="h-4 w-4" /> : <Bot className="h-4 w-4" />}
                  </div>
                  <div className={`px-4 py-2.5 rounded-2xl ${
                    msg.role === 'user'
                      ? 'bg-emerald-500 text-white rounded-br-md'
                      : 'bg-white text-gray-800 shadow-sm border border-gray-100 rounded-bl-md'
                  }`}>
                    <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                    {msg.isVoice && msg.role === 'user' && (
                      <div className="mt-1 flex items-center gap-1 text-xs opacity-75">
                        <Mic className="h-3 w-3" /> Voice message
                      </div>
                    )}
                    {msg.audio && msg.role === 'assistant' && (
                      <button
                        onClick={() => playAudio(msg.audio)}
                        className="mt-2 flex items-center gap-1 text-xs text-emerald-600 hover:text-emerald-700"
                      >
                        <Volume2 className="h-3 w-3" /> Play response
                      </button>
                    )}
                    {msg.action && (
                      <div className="mt-2 pt-2 border-t border-gray-200 text-xs text-emerald-600">
                        ✓ Action completed
                      </div>
                    )}
                  </div>
                </div>
              </div>
            ))}
            
            {isLoading && (
              <div className="flex justify-start">
                <div className="flex gap-2">
                  <div className="w-8 h-8 rounded-full bg-gradient-to-br from-purple-500 to-indigo-500 flex items-center justify-center">
                    <Bot className="h-4 w-4 text-white" />
                  </div>
                  <div className="px-4 py-3 rounded-2xl bg-white shadow-sm border border-gray-100 rounded-bl-md">
                    <Loader2 className="h-5 w-5 animate-spin text-emerald-500" />
                  </div>
                </div>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length <= 1 && (
            <div className="px-4 py-2 bg-gray-50 border-t border-gray-100">
              <p className="text-xs text-gray-500 mb-2">Quick actions:</p>
              <div className="flex flex-wrap gap-2">
                {quickActions.map((action, idx) => (
                  <button
                    key={idx}
                    onClick={() => {
                      setInput(action.message);
                      setTimeout(() => sendMessage(), 100);
                    }}
                    className="text-xs px-3 py-1.5 bg-white border border-gray-200 rounded-full hover:border-emerald-300 hover:bg-emerald-50 transition"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input */}
          <div className="p-4 bg-white border-t border-gray-200">
            {/* Voice toggle */}
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs text-gray-500">
                {isRecording ? '🔴 Recording...' : 'Type or speak your message'}
              </span>
              <button
                onClick={() => setVoiceEnabled(!voiceEnabled)}
                className={`text-xs flex items-center gap-1 px-2 py-1 rounded-full ${
                  voiceEnabled ? 'bg-emerald-100 text-emerald-700' : 'bg-gray-100 text-gray-500'
                }`}
                title={voiceEnabled ? 'Voice responses on' : 'Voice responses off'}
              >
                {voiceEnabled ? <Volume2 className="h-3 w-3" /> : <VolumeX className="h-3 w-3" />}
                {voiceEnabled ? 'Voice on' : 'Voice off'}
              </button>
            </div>
            
            <div className="flex items-center gap-2">
              {/* Voice button */}
              <button
                onMouseDown={startRecording}
                onMouseUp={stopRecording}
                onMouseLeave={stopRecording}
                onTouchStart={startRecording}
                onTouchEnd={stopRecording}
                disabled={isLoading}
                className={`w-10 h-10 rounded-full flex items-center justify-center transition ${
                  isRecording 
                    ? 'bg-red-500 hover:bg-red-600 animate-pulse' 
                    : 'bg-gray-200 hover:bg-gray-300'
                } disabled:opacity-50`}
                title="Hold to speak"
                data-testid="ai-voice-btn"
              >
                {isRecording ? (
                  <MicOff className="h-4 w-4 text-white" />
                ) : (
                  <Mic className="h-4 w-4 text-gray-600" />
                )}
              </button>
              
              <input
                ref={inputRef}
                type="text"
                value={input}
                onChange={(e) => setInput(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type or hold mic to speak..."
                className="flex-1 px-4 py-2.5 bg-gray-100 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:bg-white transition"
                disabled={isLoading || isRecording}
                data-testid="ai-chat-input"
              />
              <button
                onClick={sendMessage}
                disabled={!input.trim() || isLoading || isRecording}
                className="w-10 h-10 rounded-full bg-emerald-500 hover:bg-emerald-600 disabled:bg-gray-300 disabled:cursor-not-allowed flex items-center justify-center transition"
                data-testid="ai-chat-send"
              >
                <Send className="h-4 w-4 text-white" />
              </button>
            </div>
            
            {isPlaying && (
              <div className="mt-2 flex items-center justify-center">
                <button
                  onClick={stopAudio}
                  className="text-xs flex items-center gap-1 px-3 py-1 bg-red-100 text-red-600 rounded-full hover:bg-red-200"
                >
                  <VolumeX className="h-3 w-3" /> Stop speaking
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default AIChatBubble;
